import { z } from 'zod';

/**
 * Schema and helpers for the article form (products and intermediate products).
 * The article properties decide which field groups are active and mandatory
 * (docs/CONCEPT_Feingebaeck_Artikeleigenschaften.md, section 5.1).
 */

export type ArticleType = 'PRODUCT' | 'INTERMEDIATE_PRODUCT';
export type ArticleOrigin = 'DOUGH' | 'PIECES';

type Translate = (key: string, options?: { defaultValue: string }) => string;

export interface ArticleSchemaContext {
  materialType: ArticleType;
  /** Oven type (STONE_OVEN, RACK_OVEN, CONTINUOUS_OVEN, FRYER) by resource type uuid. */
  ovenTypeByUuid: Record<string, string | undefined>;
}

/** The field groups of the article form and whether they are active. */
export interface ArticleGroups {
  dough: boolean;
  separation: boolean;
  oven: boolean;
  stock: boolean;
  freshness: boolean;
  longTerm: boolean;
}

export const activeGroups = (props: {
  materialType: ArticleType;
  origin?: ArticleOrigin | null;
  separated?: boolean | null;
  baked?: boolean | null;
  longRunning?: boolean | null;
}): ArticleGroups => {
  const stocked = props.materialType === 'INTERMEDIATE_PRODUCT';
  return {
    dough: props.origin !== 'PIECES',
    separation: props.origin !== 'PIECES' && props.separated === true,
    oven: props.baked === true,
    stock: stocked,
    freshness: !stocked,
    longTerm: props.longRunning === true,
  };
};

/** An article in pieces: separated itself or continuing a predecessor. Its unit is pieces. */
export const isPieceArticle = (props: { origin?: string | null; separated?: boolean | null }): boolean =>
  props.origin === 'PIECES' || props.separated === true;

/** Articles that may be a predecessor: stocked (intermediate product) and in pieces. */
export const isPredecessorCandidate = (material: {
  materialType?: string;
  origin?: string;
  separated?: boolean;
}): boolean => material.materialType === 'INTERMEDIATE_PRODUCT' && isPieceArticle(material);

const optionalNumber = z.union([z.number(), z.null()]).optional();

export const createArticleSchema = (t: Translate, context: ArticleSchemaContext) => {
  const requiredMessage = t('validation.required', { defaultValue: 'Dieses Feld ist erforderlich' });
  const greaterThanZero = t('validation.minValueGreaterThanZero', { defaultValue: 'Muss größer als 0 sein' });

  return z.object({
    name: z.string().min(1, requiredMessage),
    articleNumber: z.string().optional().nullable(),
    description: z.string().optional().nullable(),
    baseUnit: z.string().optional(),
    global: z.boolean().optional().default(false),

    // Article properties
    origin: z.enum(['DOUGH', 'PIECES']),
    predecessorUuid: z.string().optional().nullable(),
    separated: z.boolean(),
    baked: z.boolean(),
    longRunning: z.boolean(),
    longTermOffsetDays: optionalNumber,

    // Alias (products only)
    isAlias: z.boolean().optional(),
    aliasForProductUuid: z.string().optional().nullable(),

    // Dough/batter
    minDoughQuantityInKg: optionalNumber,
    maxDoughQuantityInKg: optionalNumber,
    doughBatchMethod: z.string().optional().nullable(),

    // Separation
    initialWeighInGrams: optionalNumber,
    scrapRate: optionalNumber,
    requireContiguousCampaignBatches: z.boolean().optional(),

    // Oven
    defaultOvenTypeUuid: z.string().optional().nullable(),
    piecesPerOven: optionalNumber,
    startTemperature: z.union([z.number().min(0, 'validation.minValueZero'), z.null()]).optional(),
    endTemperature: z.union([z.number().min(0, 'validation.minValueZero'), z.null()]).optional(),
    ovenBatchDetermination: z.string().optional().nullable(),
    minOvenUtilizationPercent: optionalNumber,

    // Stock (intermediate products)
    planningMode: z.enum(['AUTO', 'MANUAL']).optional(),
    minLotSize: z.union([z.number().positive(), z.null()]).optional(),
    maxLotSize: z.union([z.number().positive(), z.null()]).optional(),
    lotSizeMultiple: z.union([z.number().positive(), z.null()]).optional(),
    maxStorageDays: z.union([z.number().int().min(1, greaterThanZero), z.null()]).optional(),

    // Freshness (products)
    maxProductionLeadTimeHours: z.union([z.number().int().min(0, 'validation.minValueZero'), z.null()]).optional(),

    // Rough planning (products)
    excludedFromRoughPlanning: z.boolean().optional(),
  }).superRefine((data, ctx) => {
    const issue = (path: string, message: string = requiredMessage) =>
      ctx.addIssue({ code: z.ZodIssueCode.custom, message, path: [path] });
    const groups = activeGroups({ materialType: context.materialType, ...data });
    const isAlias = context.materialType === 'PRODUCT' && data.isAlias === true;

    if (context.materialType === 'INTERMEDIATE_PRODUCT' && !data.articleNumber?.trim()) {
      issue('articleNumber');
    }
    if (data.origin === 'PIECES' && !data.predecessorUuid) {
      issue('predecessorUuid');
    }
    if (isAlias && !data.aliasForProductUuid) {
      issue('aliasForProductUuid');
    }
    if (data.longRunning && (data.longTermOffsetDays == null || data.longTermOffsetDays < 1 || data.longTermOffsetDays > 31)) {
      issue('longTermOffsetDays', t('product.longTermOffsetDaysValidation', {
        defaultValue: 'Versatz (Tage) muss zwischen 1 und dem konfigurierten Maximum liegen.',
      }));
    }
    if (data.minLotSize != null && data.maxLotSize != null && data.minLotSize > data.maxLotSize) {
      issue('maxLotSize', t('intermediateProduct.lotSizeMinLeMax', {
        defaultValue: 'Mindestlosgröße darf nicht über Maximallosgröße liegen.',
      }));
    }

    // Alias products take their values from the real product
    if (isAlias) {
      return;
    }

    if (groups.dough) {
      if (data.minDoughQuantityInKg == null) issue('minDoughQuantityInKg');
      if (data.maxDoughQuantityInKg == null) issue('maxDoughQuantityInKg');
      if (data.minDoughQuantityInKg != null && data.maxDoughQuantityInKg != null
        && data.minDoughQuantityInKg > data.maxDoughQuantityInKg) {
        issue('maxDoughQuantityInKg', t('article.doughMinLeMax', {
          defaultValue: 'Die minimale Teigmenge darf nicht über der maximalen liegen.',
        }));
      }
    }
    if (groups.separation && (data.initialWeighInGrams == null || data.initialWeighInGrams <= 0)) {
      issue('initialWeighInGrams', greaterThanZero);
    }
    if (groups.oven) {
      if (!data.defaultOvenTypeUuid) issue('defaultOvenTypeUuid');
      if (data.piecesPerOven == null || data.piecesPerOven <= 0) issue('piecesPerOven', greaterThanZero);
      if (!data.ovenBatchDetermination) issue('ovenBatchDetermination');
      const isFryer = data.defaultOvenTypeUuid ? context.ovenTypeByUuid[data.defaultOvenTypeUuid] === 'FRYER' : false;
      if (!isFryer) {
        if (data.startTemperature == null) issue('startTemperature');
        if (data.endTemperature == null) issue('endTemperature');
      }
      if (data.ovenBatchDetermination === 'OVEN_UTILIZATION') {
        if (data.minOvenUtilizationPercent == null) {
          issue('minOvenUtilizationPercent');
        } else if (data.minOvenUtilizationPercent <= 0) {
          issue('minOvenUtilizationPercent', 'validation.minValueGreaterThanZero');
        } else if (Math.abs(Math.round(data.minOvenUtilizationPercent * 10) / 10 - data.minOvenUtilizationPercent) > 1e-6) {
          issue('minOvenUtilizationPercent', 'validation.oneDecimalPlaceRequired');
        }
      }
    }
  });
};

export type ArticleFormData = z.input<ReturnType<typeof createArticleSchema>>;

/**
 * Short text of the article properties for grids, e.g. "Teig/Masse · vereinzelt · gebacken".
 * Undeclared properties (null) are left out.
 */
export const describeArticleProperties = (
  material: { origin?: string; predecessorUuid?: string; separated?: boolean; baked?: boolean; longRunning?: boolean },
  t: Translate,
  nameByUuid: Map<string | undefined, string | undefined>,
): string => {
  const parts: string[] = [];
  if (material.origin === 'PIECES') {
    const predecessor = nameByUuid.get(material.predecessorUuid);
    parts.push(t('article.fromPredecessor', { defaultValue: 'aus Vorstufe' }) + (predecessor ? ` ${predecessor}` : ''));
  } else if (material.origin === 'DOUGH') {
    parts.push(t('article.originDough', { defaultValue: 'Teig/Masse' }));
  }
  if (material.separated) parts.push(t('article.separatedShort', { defaultValue: 'vereinzelt' }));
  if (material.baked) parts.push(t('article.bakedShort', { defaultValue: 'gebacken' }));
  if (material.longRunning) parts.push(t('product.longRunning', { defaultValue: 'Langzeit geführt' }));
  return parts.join(' · ');
};
